import {useEffect, useRef, useState} from "react";

// Scroll distance past which the scroll-to-top CTA is revealed.
export const SCROLL_TO_TOP_THRESHOLD = 100;

// Tracks whether the window is scrolled past `threshold` px.
// Updates are coalesced to one per animation frame, and the setter is skipped
// entirely while the value is unchanged. The comparison has to happen here at the
// call site rather than inside a `setIsPastThreshold` updater: React runs the
// updater eagerly only on its bailout path, which is gated on the fiber having no
// pending lanes, so during a sustained Android fling that guard never runs and
// every frame enqueues a real update -- keeping the nested-update counter alive
// until it trips error #185 (AC-766).
export const useScrollPastThreshold = (threshold: number) => {
    const [isPastThreshold, setIsPastThreshold] = useState(false);
    const lastValueRef = useRef(false);

    useEffect(() => {
        let rafId: number | null = null;

        const onScroll = () => {
            if (rafId !== null) return;
            rafId = requestAnimationFrame(() => {
                rafId = null;
                const next = window.scrollY > threshold;
                if (lastValueRef.current === next) return;
                lastValueRef.current = next;
                setIsPastThreshold(next);
            });
        };

        window.addEventListener('scroll', onScroll, {passive: true});
        return () => {
            window.removeEventListener('scroll', onScroll);
            if (rafId !== null) {
                cancelAnimationFrame(rafId);
            }
        };
    }, [threshold]);

    return isPastThreshold;
};
